import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe } from '../services/api';

const AuthContext = createContext(null);

// Read cached user instantly — no loading, no flicker
function getCachedUser() {
  try {
    const json = localStorage.getItem('user');
    return json ? JSON.parse(json) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getCachedUser);   // instant from cache

  const updateUser = useCallback((data) => {
    const u = {
      id: data.id, name: data.name, email: data.email, role: data.role,
      credits: data.credits ?? 0, subscriptionStatus: data.subscriptionStatus ?? 'Free'
    };
    setUser(u);
    localStorage.setItem('user', JSON.stringify(u));
  }, []);

  // Silently validate token in background
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getMe()
        .then(({ data }) => updateUser(data))
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        });
    }
  }, [updateUser]);

  const login = (data) => {
    localStorage.setItem('token', data.token);
    updateUser(data);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await getMe();
      updateUser(data);
    } catch { /* ignore */ }
  }, [updateUser]);

  const isAdmin = user?.role === 'admin';
  const loading = false;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);